import { Input } from '@/components/inputs';
import { RuleTraitIcon } from '@/lib/Icons';
import { ExpressionCondition } from '@/models/audiences';
import { ISelectItem } from '@/models/select';
import { format } from 'date-fns';
import { omit } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchDropdown, TraitSelect } from '../selects';
import { DatePickerInput } from '../selects/DatePickerInput';
import { PropertyOperations } from '@/services/endpoints/audiences';
import { PROPERTY_OPERATIONS_LABEL_MAPPER } from '@/features/audienceEdit_v2/constants';

type PropertyConditionProps = {
  operationList?: PropertyOperations;
  expression: ExpressionCondition;
  onChange?: (condition: ExpressionCondition) => void;
  isViewMode?: boolean;
  datasourceIds?: string[];
};

type PROPERTY_OPERATIONS_LABEL_MAPPER_KEY = keyof typeof PROPERTY_OPERATIONS_LABEL_MAPPER;

type ComparisonOption = ISelectItem & {
  id: string;
  label: string;
  displayName: string;
};

const createComparisonOptions = (operations: string[]): ComparisonOption[] => {
  return operations?.map((operation) => ({
    id: operation,
    label: PROPERTY_OPERATIONS_LABEL_MAPPER[operation as PROPERTY_OPERATIONS_LABEL_MAPPER_KEY] || operation,
    displayName: PROPERTY_OPERATIONS_LABEL_MAPPER[operation as PROPERTY_OPERATIONS_LABEL_MAPPER_KEY] || operation,
    doNotRenderValue: operation === 'NULL' || operation === 'NOT_NULL',
  }));
};

export const PropertyCondition: FC<PropertyConditionProps> = ({ expression, isViewMode, onChange, datasourceIds, operationList }) => {
  const { t } = useTranslation('audience_edit');
  const { t: tAudienceDetails } = useTranslation('audience_details');
  const { t: tAudience } = useTranslation('audience');

  const [textComparisionOptionList, numberComparisonOptionList, dateComparisonOptionList, aggregationOptionList] = useMemo(() => {
    if (!operationList) return [];
    return [
      createComparisonOptions(operationList.TEXT.operations),
      createComparisonOptions(operationList.NUMBER.operations),
      createComparisonOptions(operationList.DATE.operations),
      createComparisonOptions(operationList.NUMBER.aggregations),
    ];
  }, [operationList]);

  const retrieveComparisonOp = useCallback(
    (expression: ExpressionCondition) => {
      if (!expression) return [];
      switch (expression.dataType) {
        case 'TEXT':
          return textComparisionOptionList;
        case 'NUMBER':
          return numberComparisonOptionList;
        case 'DATE':
        case 'DATETIME':
          return dateComparisonOptionList;
        default:
          return textComparisionOptionList;
      }
    },
    [textComparisionOptionList, numberComparisonOptionList, dateComparisonOptionList]
  );

  const renderInputFromDataType = (expression: ExpressionCondition) => {
    switch (expression.dataType) {
      default:
      case 'TEXT':
        return (
          <Input
            name='value'
            id='value'
            type='text'
            className='h-[45px] w-full px-2 py-1 '
            value={expression.value}
            isViewMode={isViewMode}
            onChange={(ev) => {
              onChange?.({
                ...expression,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value: ev.target.value,
              });
            }}
          />
        );
      case 'NUMBER':
        return (
          <Input
            name='value'
            id='value'
            type='number'
            className='h-[45px] w-full px-2 py-1'
            value={expression.value == undefined ? 0 : expression.value}
            isViewMode={isViewMode}
            onChange={(ev) => {
              onChange?.({
                ...expression,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value: parseInt(ev.target.value),
              });
            }}
          />
        );
      case 'DATE':
      case 'DATETIME':
        return (
          <DatePickerInput
            className='h-[45px] w-full px-2 py-1'
            selected={expression.value ? new Date(expression.value) : undefined}
            dateFormat='P'
            isViewMode={isViewMode}
            onChange={(date) => {
              if (date instanceof Date) {
                onChange?.({
                  ...expression,
                  value: format(date, 'yyyy-MM-dd'),
                });
              }
            }}
          />
        );
    }
  };

  useEffect(() => {
    const comparisonOp = retrieveComparisonOp(expression);
    setComparisonOps(comparisonOp);
    setSelectedComparisonOp(traitDataType === expression?.dataType ? comparisonOp?.find((op) => op?.id === expression?.comparisonOp) : null);
    setTraitDataType(expression?.dataType);
  }, [expression, retrieveComparisonOp]);

  const [traitDataType, setTraitDataType] = useState(expression?.dataType);
  const [comparisonOps, setComparisonOps] = useState(retrieveComparisonOp(expression));
  const [selectedComparisonOp, setSelectedComparisonOp] = useState<ComparisonOption | null>();
  const [comparisonQuery, setComparisonQuery] = useState('');
  const [selectedAggregation, setSelectedAggregation] = useState<ComparisonOption | null>();

  const getSelectedAggregation = useCallback(() => {
    if (!selectedAggregation) {
      const noneAggregationOption = aggregationOptionList?.find((op) => op.id === 'NONE');
      return {
        ...noneAggregationOption,
        id: String(noneAggregationOption?.id),
        displayName: tAudience(noneAggregationOption?.displayName as any),
      };
    } else {
      return {
        ...selectedAggregation,
        id: String(selectedAggregation?.id),
        displayName: tAudience(selectedAggregation?.displayName as any),
      };
    }
  }, [selectedAggregation, aggregationOptionList, tAudience]);

  if (isViewMode) {
    // Compact view mode layout
    const currentComparisonOp = comparisonOps?.find((op) => op?.id === expression?.comparisonOp);
    const showValue = !currentComparisonOp?.doNotRenderValue;

    return (
      <div className='flex flex-wrap items-center gap-2 text-sm'>
        <span className='flex items-center gap-2'>
          <RuleTraitIcon className='size-4 text-brand-600' />
          <span>{tAudienceDetails('sources_and_rules.rules.haveProperty_read_view')}</span>
        </span>

        <TraitSelect
          traitId={expression?.property}
          datasourceIds={datasourceIds}
          onChange={() => {}}
          isViewMode={true}
          className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'
        />

        <span>{tAudience(currentComparisonOp?.displayName as any)}</span>

        {showValue && expression.value && (
          <span className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'>
            {expression?.dataType === 'DATE' ? format(new Date(expression?.value), 'P') : expression?.value}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className='flex w-full items-start'>
      <div className='flex w-full items-start'>
        {/* Icon and text - Fixed width section */}
        <div className='mr-4 mt-2 min-w-[180px] shrink-0 whitespace-nowrap text-sm'>
          <span className='flex items-center gap-2 font-medium'>
            <RuleTraitIcon className='inline-block size-6 text-brand-600' />
            <span className='text-sm text-primary-900'>{t('haveProperty_read')}</span>
          </span>
        </div>
        <div className='grid gap-2'>
          {/* Responsive grid for form elements */}
          <div className={`grid flex-1 grid-cols-1 items-start gap-3 md:grid-cols-4`}>
            {/* Trait/Property Select */}
            <div className='md:col-span-1'>
              <TraitSelect
                traitId={expression?.property}
                datasourceIds={datasourceIds}
                onChange={(traitItemValue) => {
                  onChange?.({
                    ...omit(expression, 'comparisonOp', 'value'),
                    ...traitItemValue,
                    comparisonOp: traitItemValue.dataType === expression.dataType ? expression?.comparisonOp : undefined,
                  });
                }}
                isViewMode={isViewMode}
                className='h-[45px]'
              />
            </div>
            {/* Aggregation Selector (only show if data type is NUMBER) */}
            {expression.dataType === 'NUMBER' && aggregationOptionList?.length > 0 && (
              <div className='md:col-span-1'>
                <SearchDropdown
                  items={aggregationOptionList}
                  placeholder={tAudience('rules.select_aggregation')}
                  item={getSelectedAggregation()}
                  setItem={(item) => {
                    setSelectedAggregation(item);
                    onChange?.({
                      ...expression,
                      aggregationType: String(item.id),
                    });
                  }}
                  loading={false}
                  openWithFocus={true}
                  acceptCustomValue={false}
                  readOnly={isViewMode}
                  className='px-2 py-1'
                >
                  {(item) => <div className='whitespace-nowrap'>{tAudience(item.displayName as any)}</div>}
                </SearchDropdown>
              </div>
            )}
            {/* Comparison Operator */}
            <div className='md:col-span-1'>
              <SearchDropdown
                items={
                  comparisonQuery?.trim()
                    ? comparisonOps?.filter((item) => item.displayName?.toLowerCase()?.includes(comparisonQuery?.trim()?.toLocaleLowerCase()))
                    : comparisonOps
                }
                item={
                  selectedComparisonOp
                    ? {
                        ...selectedComparisonOp,
                        displayName: tAudience(selectedComparisonOp.displayName),
                      }
                    : null
                }
                setItem={(opItemValue) => {
                  setSelectedComparisonOp(opItemValue);
                  onChange?.({
                    ...expression,
                    comparisonOp: String(opItemValue.id),
                  });
                }}
                setQuery={setComparisonQuery}
                loading={false}
                openWithFocus={true}
                acceptCustomValue={false}
                placeholder={t('property_condition_placeholder') as string}
                readOnly={isViewMode}
                className='px-2 py-1'
              >
                {(item) => <div className='whitespace-nowrap'>{tAudience(item.displayName as any)}</div>}
              </SearchDropdown>
            </div>

            {/* Value Input (only show if not NULL/NOT_NULL) */}
            {!comparisonOps?.find((op) => op.id === expression.comparisonOp)?.doNotRenderValue && (
              <div className='md:col-span-1'>{renderInputFromDataType(expression)}</div>
            )}
          </div>
          {expression.dataType === 'NUMBER' && aggregationOptionList?.length > 0 && (
            <p className='text-sm text-quaternary-500'>{tAudience('rules.aggregation_note')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
